import Axios from "axios";
import {getRequestHeaders} from '../api'
import {
  differenceInCalendarWeeks,
  format,
  getDate,
  getDay,
  getHours,
  getMilliseconds,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  parse,
  parseISO,
} from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { baseUrl } from "../assets/Animations";

//EVERYTHING IS STORED IN UTC TIME STRING FORMAT

export const generateNewCalendarEvent = (
  e,
  schoolAbbreviation,
  userName,
  schoolTimeZone
) => {
  let timeZoneBasedTime = convertUTCStringToTimezoneString(
    e,
    getSchoolIANATimezone(schoolAbbreviation)
  );

  return {
    id: Math.random().toString(16).slice(2),
    lessonId: Math.random().toString(16).slice(2),
    name: "",
    time: null,
    dateTime: timeZoneBasedTime,
    timeZone: schoolTimeZone,
    recurring: {
      isOn: false,
      frequency: "none",
      interval: 0,
    },
    recurringExceptions: [],
    recurringEndDate: "",
    lessonLength: 30,
    familyId: "",
    familyName: "",
    parentName: "",
    familyAddress: {},
    studentId: "",
    studentName: "",
    studentDOB: "",
    familyPhoneNumber: "",
    familyEmail: "",
    teacherId: "",
    teacherName: "",
    teacherImageUrl: "",
    school: schoolAbbreviation,
    waveCustomerId: "",
    isIntro: false,
    isMakeupLesson: false,
    isAvailabilityPlaceholder: false,
    isPendingOffer: false,
    createdBy: userName,
    createdOn: new Date(),
  };
};

export const generateNewPendingOffer = (e, schoolAbbreviation, userName) => {
  return {
    id: Math.random().toString(16).slice(2),
    lessonId: Math.random().toString(16).slice(2),
    name: "",
    time: null,
    dateTime: e ? new Date(e) : null,
    recurring: {
      isOn: true,
      frequency: "weekly",
      interval: 0,
    },
    recurringExceptions: [],
    recurringEndDate: "",
    lessonLength: 30,
    familyId: "",
    familyName: "",
    parentName: "",
    familyAddress: {},
    studentId: "",
    studentName: "",
    studentDOB: "",
    familyPhoneNumber: "",
    familyEmail: "",
    teacherId: "",
    teacherName: "",
    teacherImageUrl: "",
    school: schoolAbbreviation,
    waveCustomerId: "",
    isIntro: false,
    isMakeupLesson: false,
    isAvailabilityPlaceholder: false,
    isPendingOffer: true,
    createdBy: userName,
    createdOn: new Date(),
  };
};

export const truncate = (str, length) => {
  return str.length > length ? str.substring(0, length) + "..." : str;
};

export const convertUTCStringToTimezoneString = (utcTime, timezone) => {
  const date = new Date(utcTime);
  const localDate = utcToZonedTime(date, timezone);
  return format(localDate, "yyyy-MM-dd'T'HH:mm:ss", { timeZone: timezone });
};

export const convertStringToUTCString = (datetime, timezone) => {
  const utcDate = zonedTimeToUtc(datetime, timezone);
  return utcDate.toISOString();
};

export const getAdminIANATimezone = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return userTimezone;
};

export const convertDateStringIntoDateObject = (dateString) => {
  // Get the current timezone offset in minutes
  const timezoneOffset = new Date().getTimezoneOffset();

  // Parse the date string and adjust for the timezone offset
  const myDate = new Date(Date.parse(dateString) + timezoneOffset * 60 * 1000);

  return myDate;
};

export const getSchoolIANATimezone = (schoolAbbreviation) => {
  switch (schoolAbbreviation) {
    case "mma":
      return "America/Denver";
    case "lsma":
      return "America/Chicago";
    case "tma":
      return "America/Detroit";
    case "mor":
      return "America/Los_Angeles";
    case "psma":
      return "America/Los_Angeles";
    default:
      throw new Error("Invalid school abbreviation");
  }
};

export const editAllEvents = async (
  eventObjectToEdit,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  Axios.put(`${baseUrl}/calendar/${eventObjectToEdit.id}`, {
    event: utcShiftedEvent,
  }, getRequestHeaders())
    .then((res) => {
      stateUpdateCallback((prev) =>
        prev.map((e) => (e.id === res.data.id ? res.data : e))
      );
      eventToggle(false);
      socket.emit("closeAndSaveCalendarEvent-all-events", {
        emitter: name,
        packet: res.data,
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

export const editThisAndFollowingEvents = async (
  eventObjectToEdit,
  editedEventObjectsOriginalDate,
  stateUpdateCallback,
  eventToggle,
  socket,
  name,
  myHeader
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  let endedEventObject = {
    ...utcShiftedEvent,
    recurringEndDate: editedEventObjectsOriginalDate
      ? convertStringToUTCString(
          editedEventObjectsOriginalDate,
          getSchoolIANATimezone(eventObjectToEdit.school)
        )
      : "",
  };
  if (utcShiftedEvent.isTheOriginalEvent) {
    let deleteTheOriginalEvent = Axios.delete(`${baseUrl}/calendar/${utcShiftedEvent.lessonId}`, getRequestHeaders());
    let createNewFollowingEvents = Axios.post(`${baseUrl}/calendar`, {
      event: { ...utcShiftedEvent, id: Math.random().toString(16).slice(2) },
    }, getRequestHeaders());

    return Promise.all([deleteTheOriginalEvent, createNewFollowingEvents])
      .then((values) => {
        stateUpdateCallback((prev) => [
          ...prev.filter((e) => e.lessonId !== eventObjectToEdit.lessonId),
        ]);
        // This is a hack to get around the fact that the above filter removed all the events from the state before the new event was added to the state
        setTimeout(() => {
          stateUpdateCallback((prev) => [...prev, values[1].data]);
        }, 0);
        //------------------------------
        eventToggle(false);
        socket.emit("closeAndSaveCalendarEvent-following-events", {
          emitter: name,
          packet: { option: 1, response: values },
        });
      })
      .catch((err) => console.log(err));
  } else {
    let endOriginalEvent = Axios.put(
      `${baseUrl}/calendar/end/${endedEventObject.id}`,
      {
        recurringEndDate: editedEventObjectsOriginalDate
          ? convertStringToUTCString(
              editedEventObjectsOriginalDate,
              getSchoolIANATimezone(eventObjectToEdit.school)
            )
          : "",
        recurringExceptions: eventObjectToEdit.recurringExceptions,
      },
      myHeader
    );
    let createNewFollowingEvents = Axios.post(
      `${baseUrl}/calendar`,
      {
        event: { ...utcShiftedEvent, id: Math.random().toString(16).slice(2) },
      },
      myHeader
    );

    return Promise.all([endOriginalEvent, createNewFollowingEvents])
      .then((values) => {
        stateUpdateCallback((prev) => [...prev, values[1].data]);
        stateUpdateCallback((prev) => [
          ...prev.map((e) => (e.id === values[0].data.id ? values[0].data : e)),
        ]);
        eventToggle(false);
        socket.emit("closeAndSaveCalendarEvent-following-events", {
          emitter: name,
          packet: { option: 2, response: values },
        });
      })
      .catch((err) => console.log(err));
  }
};

export const editThisEvent = async (
  eventObjectToEdit,
  editedEventObjectsOriginalDate,
  stateUpdateCallback,
  eventToggle,
  socket,
  name,
  myHeader
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  let newEditedEventObject = {
    ...utcShiftedEvent,
    id: Math.random().toString(16).slice(2),
    recurring: { isOn: false, frequency: "none", interval: 0 },
  };
  let endAndSetRecurringException = Axios.put(
    `${baseUrl}/calendar/end/${utcShiftedEvent.id}`,
    {
      recurringExceptions: [
        ...utcShiftedEvent.recurringExceptions,
        editedEventObjectsOriginalDate,
      ],
      recurringEndDate: utcShiftedEvent.recurringEndDate
        ? convertStringToUTCString(
            utcShiftedEvent.recurringEndDate,
            getSchoolIANATimezone(eventObjectToEdit.school)
          )
        : "",
    },
    myHeader
  );
  let createNewEventNonRecurringEvent = Axios.post(
    `${baseUrl}/calendar`,
    { event: newEditedEventObject },
    myHeader
  );

  return Promise.all([
    endAndSetRecurringException,
    createNewEventNonRecurringEvent,
  ])
    .then((values) => {
      if (values[0]) {
        stateUpdateCallback((prev) => [
          ...prev.map((e) => (e.id === values[0].data.id ? values[0].data : e)),
        ]);
      }
      stateUpdateCallback((prev) => [...prev, values[1].data]);
      eventToggle(false);
      socket.emit("closeAndSaveCalendarEvent-this-event", {
        emitter: name,
        packet: values,
      });
    })
    .catch((err) => console.log(err));
};

export const editSoloEvent = async (
  eventObjectToEdit,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  return Axios.put(`${baseUrl}/calendar/${utcShiftedEvent.id}`, {
    event: utcShiftedEvent,
  },getRequestHeaders())
    .then((res) => {
      stateUpdateCallback((prev) => [
        ...prev.map((e) => (e.id === res.data.id ? res.data : e)),
      ]);
      eventToggle(false);
      socket.emit("closeAndSaveCalendarEvent-solo-events", {
        emitter: name,
        packet: { option: 1, response: res.data },
      });
    })
    .catch((err) => console.log(err));
};

export const filterOutPendingOffersAndUpdateEvent = (events, response) => {
  let updatedEvents = events.map((e) => (e.id === response.id ? response : e));
  let filteredEvents = [];
  // Filter out any calendar events that have the same studentId, teacherId, id, and isPendingOffer as the response data
  for (let i = 0; i < updatedEvents.length; i++) {
    if (updatedEvents[i].isPendingOffer) {
      if (updatedEvents[i].teacherId === response.teacherId) {
        if (updatedEvents[i].studentId === response.studentId) {
          //don't add this event
        } else {
          filteredEvents.push(updatedEvents[i]);
        }
      } else {
        filteredEvents.push(updatedEvents[i]);
      }
    } else {
      filteredEvents.push(updatedEvents[i]);
    }
  }
  return filteredEvents;
};

export const editSoloEventAfterOfferChange = async (
  eventObjectToEdit,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  return Axios.put(`${baseUrl}/calendar/pending-events/${utcShiftedEvent.id}`, {
    event: utcShiftedEvent,
  }, getRequestHeaders())
    .then((res) => {
      stateUpdateCallback((prev) =>
        filterOutPendingOffersAndUpdateEvent(prev, res.data)
      );
      eventToggle(false);
      socket.emit("closeAndSaveCalendarEvent-solo-events", {
        emitter: name,
        packet: { option: 2, response: res.data },
      });
    })
    .catch((err) => console.log(err));
};

export const deleteAllEvents = async (
  eventObjectToEdit,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  Axios.delete(`${baseUrl}/calendar/${utcShiftedEvent.lessonId}`, getRequestHeaders())
    .then((res) => {
      eventToggle(false);
      stateUpdateCallback((prev) => [
        ...prev.filter((e) => e.lessonId !== utcShiftedEvent.lessonId),
      ]);
      socket.emit("closeAndDeleteCalendarEvent", {
        emitter: name,
        packet: utcShiftedEvent,
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

export const deleteThisAndFollowEvents = async (
  eventObjectToEdit,
  editedEventObjectsOriginalDate,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  let utcShiftedEvent = {
    ...eventObjectToEdit,
    dateTime: convertStringToUTCString(
      eventObjectToEdit.dateTime,
      getSchoolIANATimezone(eventObjectToEdit.school)
    ),
  };
  if (utcShiftedEvent.isTheOriginalEvent) {
    return Axios.delete(`${baseUrl}/calendar/${utcShiftedEvent.lessonId}`, getRequestHeaders())
      .then((res) => {
        eventToggle(false);
        stateUpdateCallback((prev) => [
          ...prev.filter((e) => e.lessonId !== utcShiftedEvent.lessonId),
        ]);
        socket.emit("closeAndDeleteCalendarEvent-following-events", {
          emitter: name,
          packet: { option: 1, response: utcShiftedEvent },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  } else {
    let endedEventObject = {
      ...utcShiftedEvent,
      recurringEndDate: editedEventObjectsOriginalDate
        ? convertStringToUTCString(
            editedEventObjectsOriginalDate,
            getSchoolIANATimezone(eventObjectToEdit.school)
          )
        : "",
    };
    return Axios.put(
      `${baseUrl}/calendar/end-delete/${endedEventObject.lessonId}`,
      {
        recurringExceptions: utcShiftedEvent.recurringExceptions,
        recurringEndDate: editedEventObjectsOriginalDate,
      }, getRequestHeaders()
    )
      .then((res) => {
        eventToggle(false);
        res.data.forEach((event) => {
          stateUpdateCallback((prev) => [
            ...prev.map((e) => (e.id === event.id ? event : e)),
          ]);
          socket.emit("closeAndDeleteCalendarEvent-following-events", {
            emitter: name,
            packet: { option: 2, response: event },
          });
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }
};

export const deleteThisEvent = async (
  eventObjectToEdit,
  editedEventObjectsOriginalDate,
  stateUpdateCallback,
  eventToggle,
  socket,
  name,
  myHeader
) => {
  if (eventObjectToEdit.recurring.isOn) {
    return Axios.put(
      `${baseUrl}/calendar/end/${eventObjectToEdit.id}`,
      {
        recurringExceptions: [
          ...eventObjectToEdit.recurringExceptions,
          convertStringToUTCString(
            editedEventObjectsOriginalDate,
            getSchoolIANATimezone(eventObjectToEdit.school)
          ),
        ],
        recurringEndDate: eventObjectToEdit.recurringEndDate
          ? convertStringToUTCString(
              eventObjectToEdit.recurringEndDate,
              getSchoolIANATimezone(eventObjectToEdit.school)
            )
          : "",
      },
      myHeader
    )
      .then((res) => {
        eventToggle(false);
        stateUpdateCallback((prev) => [
          ...prev.map((e) => (e.id === res.data.id ? res.data : e)),
        ]);
        socket.emit("closeAndDeleteCalendarEvent-this-event", {
          emitter: name,
          packet: { option: 1, response: res.data },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  } else {
    return Axios.delete(`${baseUrl}/calendar-solo/${eventObjectToEdit.id}`, getRequestHeaders())
      .then((res) => {
        eventToggle(false);
        stateUpdateCallback((prev) => [
          ...prev.filter((e) => e.id !== eventObjectToEdit.id),
        ]);
        socket.emit("closeAndDeleteCalendarEvent-this-event", {
          emitter: name,
          packet: { option: 2, response: eventObjectToEdit },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }
};

export const deleteSoloEvent = async (
  eventObjectToEdit,
  stateUpdateCallback,
  eventToggle,
  socket,
  name
) => {
  return Axios.delete(`${baseUrl}/calendar-solo/${eventObjectToEdit.id}`, getRequestHeaders())
    .then((res) => {
      eventToggle(false);
      stateUpdateCallback((prev) => [
        ...prev.filter((e) => e.id !== eventObjectToEdit.id),
      ]);
      socket.emit("closeAndDeleteCalendarEvent", {
        emitter: name,
        packet: eventObjectToEdit,
      });
    })
    .catch((err) => {
      console.log(err);
    });
};

const generateDateObjectForPeriodOfTime = (daysOfTheDisplayedMonth) => {
  let newCalendarObject = {};
  daysOfTheDisplayedMonth.forEach((day) => {
    newCalendarObject[format(day, "yyyy-MM-dd")] = {
      events: [],
      availabilities: [],
    };
  });
  return newCalendarObject;
};

const generateUTCDateTimeForTeacherAvailability = (
  day,
  availability,
  school
) => {
  // Check if both availability.start and availability.end are valid times
  const startTime = parse(availability.start, "H:mm", new Date());
  const endTime = parse(availability.end, "H:mm", new Date());

  if (isNaN(startTime) || isNaN(endTime)) {
    return null;
  }

  const formattedTime = format(startTime, "HH:mm");
  const formattedTimeWithDummyDate = format(
    new Date(`2000-01-01T${formattedTime}`),
    "HH:mm:ss"
  );
  const availabilityLocalTimeString = `${format(
    day,
    "yyyy-MM-dd"
  )}T${formattedTimeWithDummyDate}`;
  return convertStringToUTCString(
    availabilityLocalTimeString,
    getSchoolIANATimezone(school)
  );
};

const generateAvailabilityLength = (dayAvailability) => {
  if(dayAvailability.end && dayAvailability.start) {
    if(dayAvailability.end.includes(":") && dayAvailability.start.includes(":")) {
      return Math.floor(
        (dayAvailability.end.split(":")[0] -
          dayAvailability.start.split(":")[0]) *
          60
      )
    }
  }
}


const addTeachersGhostAvailabilityToCalendarObject = (
  teachersToDisplayObject,
  daysOfTheDisplayedMonth,
  nonStateEventList
) => {
  Object.keys(teachersToDisplayObject).map((name) => {
    if (
      teachersToDisplayObject[name].displayed &&
      teachersToDisplayObject[name].WantsMoreStudents
    ) {
      Object.keys(teachersToDisplayObject[name].Availability).map((dayName) => {
        //IF DAY NAME EQUALS AVAIL NAME
        daysOfTheDisplayedMonth.forEach((day) => {
          let formattedDayName = format(new Date(day), "EEEE");
          if (dayName === formattedDayName) {
            //CREATE MOCK EVENT THAT IS JUST AVAILABILITY CORRECT LENGTH
            let dayAvailSections = teachersToDisplayObject[name].Availability[dayName];
            for (let i = 0; i < dayAvailSections.length; i++) {
              let newTestEvent = {
                id: Math.random().toString(16).slice(2),
                lessonId: Math.random().toString(16).slice(2),
                name: `${name.split(" ")[0]} Availability`,
                time: dayAvailSections[i].start,
                dateTime: day
                  ? generateUTCDateTimeForTeacherAvailability(
                      day,
                      dayAvailSections[i],
                      teachersToDisplayObject[name].School
                    )
                  : null,
                recurring: {
                  isOn: false,
                  frequency: "none",
                  interval: 0,
                },
                recurringExceptions: [],
                recurringEndDate: "",
                lessonLength: generateAvailabilityLength(dayAvailSections[i]),
                familyId: "",
                familyName: "",
                parentName: "",
                familyAddress: {},
                studentId: "",
                studentName: "",
                studentDOB: "",
                familyPhoneNumber: "",
                familyEmail: "",
                teacherId: "",
                teacherName: name,
                teacherImageUrl: "",
                school: teachersToDisplayObject[name].School,
                waveCustomerId: "",
                isIntro: false,
                isMakeupLesson: false,
                isAvailabilityPlaceholder: true,
                createdBy: "auto-generated",
                createdOn: new Date(),
              };
              nonStateEventList.push(newTestEvent);
            }
          }
        });
      });
    }
  });
  return nonStateEventList;
};

const checkForRecurringExceptions = (evn) => {
  let isARecurringExceptionDate = false;
  evn.recurringExceptions.forEach((skipDate) => {
    if (isSameDay(parseISO(skipDate), new Date(evn.dateTime))) {
      isARecurringExceptionDate = true;
    }
  });
  //CHECK IF RECURRING END DATE IS BEFORE THE CURRENT DATE
  if (evn.recurringEndDate !== "") {
    if (
      isBefore(
        new Date(
          convertUTCStringToTimezoneString(
            evn.recurringEndDate,
            getSchoolIANATimezone(evn.school)
          )
        ),
        new Date(evn.dateTime)
      )
    ) {
      isARecurringExceptionDate = true;
    }
  }
  return isARecurringExceptionDate;
};

const addEventToCalendar = (
  evn,
  teachersToDisplayObject,
  newCalendarObject
) => {
  if (!checkForRecurringExceptions(evn)) {
    const { teacherName, dateTime } = evn;
    const teacherColor = teachersToDisplayObject[teacherName].color;
    const formattedDate = format(new Date(dateTime), "yyyy-MM-dd");

    evn.color = teacherColor;
    evn.isTheOriginalEvent = true;

    if (newCalendarObject[formattedDate]) {
      newCalendarObject[formattedDate].events.push({ ...evn });
    }
  }
  return newCalendarObject;
};

const turnRecurringDatesIntoString = (d, evn) => {
  return format(
    new Date(
      getYear(d),
      getMonth(d),
      getDate(d),
      getHours(parseISO(evn.dateTime)),
      getMinutes(parseISO(evn.dateTime)),
      getSeconds(parseISO(evn.dateTime)),
      getMilliseconds(parseISO(evn.dateTime))
    ),
    "yyyy-MM-dd'T'HH:mm:ss"
  );
};

const addAnyRecurringDatesToCalendarObject = (
  evn,
  teachersToDisplayObject,
  newCalendarObject,
  d
) => {
  if (getDay(d) === getDay(parseISO(evn.dateTime))) {
    let found = false;
    evn.recurringExceptions.forEach((evnSkipDate) => {
      if (
        isSameDay(
          parseISO(evnSkipDate),
          new Date(getYear(d), getMonth(d), getDate(d))
        )
      ) {
        found = true;
      }
    });
    if (evn.recurring.frequency === "every-other-week") {
      if (!found) {
        if (
          differenceInCalendarWeeks(new Date(d), new Date(evn.dateTime)) % 2 ===
          0
        ) {
          evn.color = teachersToDisplayObject[evn.teacherName].color;
          newCalendarObject[format(d, "yyyy-MM-dd")].events.push({
            ...evn,
            isIntro: false,
            dateTime: turnRecurringDatesIntoString(d, evn),
          });
        }
      }
    } else if (evn.recurring.frequency === "weekly") {
      if (!found) {
        evn.color = teachersToDisplayObject[evn.teacherName].color;
        newCalendarObject[format(d, "yyyy-MM-dd")].events.push({
          ...evn,
          isIntro: false,
          dateTime: turnRecurringDatesIntoString(d, evn),
        });
      }
    }
  }
  return newCalendarObject;
};

const putEventsOnEachDayOfTheDisplayedTimePeriod = (
  daysOfTheDisplayedMonth,
  evn,
  teachersToDisplayObject,
  newCalendarObject
) => {
  daysOfTheDisplayedMonth.forEach((d) => {
    if (
      isAfter(
        new Date(d),
        parseISO(
          convertUTCStringToTimezoneString(
            evn.dateTime,
            getSchoolIANATimezone(evn.school)
          )
        )
      )
    ) {
      let tempGhostEventDateTime = new Date(
        getYear(d),
        getMonth(d),
        getDate(d),
        getHours(parseISO(evn.dateTime)),
        getMinutes(parseISO(evn.dateTime)),
        getSeconds(parseISO(evn.dateTime)),
        getMilliseconds(parseISO(evn.dateTime))
      );
      evn.isTheOriginalEvent = false;
      if (evn.recurringEndDate !== "") {
        if (
          isBefore(
            tempGhostEventDateTime,
            parseISO(
              convertUTCStringToTimezoneString(
                evn.recurringEndDate,
                getSchoolIANATimezone(evn.school)
              )
            )
          )
        ) {
          newCalendarObject = addAnyRecurringDatesToCalendarObject(
            evn,
            teachersToDisplayObject,
            newCalendarObject,
            d
          );
        }
      } else {
        newCalendarObject = addAnyRecurringDatesToCalendarObject(
          evn,
          teachersToDisplayObject,
          newCalendarObject,
          d
        );
      }
    }
  });
  return newCalendarObject;
};

export const changeDateTimeFromUTCStringToSchoolTimeZone = (event) => {
  let newEvent = { ...event };
  newEvent.dateTime = convertUTCStringToTimezoneString(
    event.dateTime,
    getSchoolIANATimezone(event.school)
  );
  newEvent.recurringEndDate =
    newEvent.recurringEndDate !== ""
      ? convertUTCStringToTimezoneString(
          event.recurringEndDate,
          getSchoolIANATimezone(event.school)
        )
      : "";
  return newEvent;
};

export const createAndOrganizeAllCalendarEventsForDisplayedPeriodOfTime = (
  dayOfDisplayedPeriodOfTime,
  teachers,
  allEvents,
  teachersToDisplayObject,
  setCalendarDaysWithEvents,
  schoolTimeZone
) => {
  let nonStateEventList = [...allEvents];
  let newCalendarObject = generateDateObjectForPeriodOfTime(
    dayOfDisplayedPeriodOfTime
  );
  if (teachers.length > 0) {
    nonStateEventList = addTeachersGhostAvailabilityToCalendarObject(
      teachersToDisplayObject,
      dayOfDisplayedPeriodOfTime,
      nonStateEventList
    );
    nonStateEventList.forEach((utcTimeEvent) => {
      let evn = changeDateTimeFromUTCStringToSchoolTimeZone(utcTimeEvent);
      if (
        teachersToDisplayObject[evn.teacherName] &&
        teachersToDisplayObject[evn.teacherName].displayed
      ) {
        if (evn.recurring.isOn) {
          newCalendarObject = addEventToCalendar(
            evn,
            teachersToDisplayObject,
            newCalendarObject
          );
          newCalendarObject = putEventsOnEachDayOfTheDisplayedTimePeriod(
            dayOfDisplayedPeriodOfTime,
            evn,
            teachersToDisplayObject,
            newCalendarObject
          );
        } else {
          evn.color = teachersToDisplayObject[evn.teacherName].color;
          if (evn.isAvailabilityPlaceholder) {
            newCalendarObject[format(parseISO(evn.dateTime), "yyyy-MM-dd")] &&
              newCalendarObject[
                format(parseISO(evn.dateTime), "yyyy-MM-dd")
              ].availabilities.push(evn);
          } else {
            if (evn.recurringEndDate !== "") {
              if (
                isAfter(new Date(evn.recurringEndDate), new Date(evn.dateTime))
              ) {
                newCalendarObject[
                  format(parseISO(evn.dateTime), "yyyy-MM-dd")
                ] &&
                  newCalendarObject[
                    format(parseISO(evn.dateTime), "yyyy-MM-dd")
                  ].events.push(evn);
              }
            } else {
              newCalendarObject[format(parseISO(evn.dateTime), "yyyy-MM-dd")] &&
                newCalendarObject[
                  format(parseISO(evn.dateTime), "yyyy-MM-dd")
                ].events.push(evn);
            }
          }
        }
      }
    });
  }
  setCalendarDaysWithEvents({ ...newCalendarObject });
};

//----------------------------------------------------------------------------------------------------

const formatCalendarObjectBasedOnDisplayedDays = (displayedDays) => {
  let formattedObject = {};
  displayedDays.forEach((day) => {
    formattedObject[format(new Date(day), "yyyy-MM-dd")] = {
      events: [],
      availabilities: [],
    };
  });
  return formattedObject;
};

const checkForNoRecurringExceptionDates = (event, date) => {
  let noneFound = true;
  event.recurringExceptions.forEach((skipDate) => {
    if (
      isSameDay(
        new Date(skipDate),
        new Date(
          getYear(new Date(date)),
          getMonth(new Date(date)),
          getDate(new Date(date))
        )
      )
    ) {
      noneFound = false;
    }
  });
  return noneFound;
};

const addNonRecurringEventToCalendarObject = (event, newCalendarObject) => {
  event.color = defaultTeacherEventColorSetup;
  event.isTheOriginalEvent = true;
  newCalendarObject[format(new Date(event.dateTime), "yyyy-MM-dd")] &&
    newCalendarObject[
      format(new Date(event.dateTime), "yyyy-MM-dd")
    ].events.push({ ...event });
  return newCalendarObject;
};

const addRecurringEventsGhostToCalendarObject = (
  event,
  day,
  newCalendarObject
) => {
  event.color = defaultTeacherEventColorSetup;
  newCalendarObject[format(day, "yyyy-MM-dd")].events.push({
    ...event,
    isIntro: false,
    dateTime: turnRecurringDatesIntoString(day, event),
  });
  return newCalendarObject;
};

const addAvailabilityPlaceholder = (newCalendarObject, event) => {
  newCalendarObject[format(new Date(event.dateTime), "yyyy-MM-dd")] &&
    newCalendarObject[
      format(new Date(event.dateTime), "yyyy-MM-dd")
    ].availabilities.push({ ...event, color: defaultTeacherEventColorSetup });
  return newCalendarObject;
};

const addRecurringEventsToDisplayedDays = (
  newCalendarObject,
  evn,
  daysOnDisplay
) => {
  if (checkForNoRecurringExceptionDates(evn, evn.dateTime)) {
    newCalendarObject = addNonRecurringEventToCalendarObject(
      evn,
      newCalendarObject
    );
  }
  daysOnDisplay.forEach((d) => {
    if (isAfter(d, new Date(evn.dateTime))) {
      evn.isTheOriginalEvent = false;
      //IF THERE IS A RECURRING END DATE
      if (evn.recurringEndDate !== "") {
        if (
          isBefore(
            new Date(
              getYear(d),
              getMonth(d),
              getDate(d),
              getHours(new Date(evn.dateTime)),
              getMinutes(new Date(evn.dateTime)),
              getSeconds(new Date(evn.dateTime)),
              getMilliseconds(new Date(evn.dateTime))
            ),
            new Date(evn.recurringEndDate)
          )
        ) {
          if (getDay(d) === getDay(new Date(evn.dateTime))) {
            if (evn.recurring.frequency === "every-other-week") {
              if (
                checkForNoRecurringExceptionDates(evn, d) &&
                differenceInCalendarWeeks(new Date(d), new Date(evn.dateTime)) %
                  2 ===
                  0
              )
                newCalendarObject = addRecurringEventsGhostToCalendarObject(
                  evn,
                  d,
                  newCalendarObject
                );
            } else if (evn.recurring.frequency === "weekly") {
              if (checkForNoRecurringExceptionDates(evn, d))
                newCalendarObject = addRecurringEventsGhostToCalendarObject(
                  evn,
                  d,
                  newCalendarObject
                );
            }
          }
        }
      } else {
        if (getDay(d) === getDay(new Date(evn.dateTime))) {
          if (evn.recurring.frequency === "every-other-week") {
            if (
              checkForNoRecurringExceptionDates(evn, d) &&
              differenceInCalendarWeeks(new Date(d), new Date(evn.dateTime)) %
                2 ===
                0
            )
              newCalendarObject = addRecurringEventsGhostToCalendarObject(
                evn,
                d,
                newCalendarObject
              );
          } else if (evn.recurring.frequency === "weekly") {
            if (checkForNoRecurringExceptionDates(evn, d))
              newCalendarObject = addRecurringEventsGhostToCalendarObject(
                evn,
                d,
                newCalendarObject
              );
          }
        }
      }
    }
  });
  return newCalendarObject;
};

let defaultTeacherEventColorSetup = {
  name: "Blue",
  bgColor200: "bg-blue-200",
  bgColor500: "bg-blue-500",
  bgColor800: "bg-blue-800",
  textColor200: "text-blue-200",
  textColor500: "text-blue-500",
  textColor800: "text-blue-800",
  borderColor200: "border-blue-200",
  borderColor500: "border-blue-500",
  borderColor800: "border-blue-800",
  selectedColor: "ring-blue-200",
};

export const createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime = (
  daysOfTheDisplayedMonth,
  allEvents,
  setCalendarDaysWithEvents
) => {
  let newCalendarObject = formatCalendarObjectBasedOnDisplayedDays(
    daysOfTheDisplayedMonth
  );
  allEvents.forEach((utcDateObject) => {
    let event = changeDateTimeFromUTCStringToSchoolTimeZone(utcDateObject);
    if (event.recurring.isOn) {
      if (event.recurringEndDate !== "") {
        if (
          isAfter(new Date(event.recurringEndDate), new Date(event.dateTime))
        ) {
          newCalendarObject = addRecurringEventsToDisplayedDays(
            newCalendarObject,
            event,
            daysOfTheDisplayedMonth
          );
        }
      } else {
        newCalendarObject = addRecurringEventsToDisplayedDays(
          newCalendarObject,
          event,
          daysOfTheDisplayedMonth
        );
      }
    } else {
      if (event.recurringEndDate !== "") {
        if (
          isAfter(new Date(event.recurringEndDate), new Date(event.dateTime))
        ) {
          newCalendarObject = event.isAvailabilityPlaceholder
            ? addAvailabilityPlaceholder(newCalendarObject, event)
            : addNonRecurringEventToCalendarObject(
                { ...event, color: defaultTeacherEventColorSetup },
                newCalendarObject
              );
        }
      } else {
        newCalendarObject = event.isAvailabilityPlaceholder
          ? addAvailabilityPlaceholder(newCalendarObject, event)
          : addNonRecurringEventToCalendarObject(
              { ...event, color: defaultTeacherEventColorSetup },
              newCalendarObject
            );
      }
    }
  });
  setCalendarDaysWithEvents({ ...newCalendarObject });
};

//----------------------------------------------------------------------------------------------------

export const createEventObjectForTeacherWeekView = (
  displayedWeek,
  calendarDaysWithEvents,
  setWeekViewEventObject
) => {
  let blankArr = [];
  displayedWeek.forEach((date, index) => {
    if (calendarDaysWithEvents[format(date, "yyyy-MM-dd")]) {
      calendarDaysWithEvents[format(date, "yyyy-MM-dd")].events.forEach(
        (event) => {
          event.span = event.lessonLength / 5;
          let hours = getHours(new Date(event.dateTime));
          let minutes = getMinutes(new Date(event.dateTime));
          event.rowNumber = Math.round(hours * 12 + minutes / 5 + 2);
          event.rowLength = event.lessonLength / 5;
          event.index = index + 1;
          blankArr.push(event);
        }
      );
      calendarDaysWithEvents[format(date, "yyyy-MM-dd")].availabilities.forEach(
        (event) => {
          event.span = event.lessonLength / 5;
          let hours = getHours(new Date(event.dateTime));
          let minutes = getMinutes(new Date(event.dateTime));
          event.rowNumber = Math.round(hours * 12 + minutes / 5 + 2);
          event.rowLength = event.lessonLength / 5;
          event.index = index + 1;
          blankArr.push(event);
        }
      );
    }
  });
  setWeekViewEventObject([...blankArr]);
};

export const createEventObjectForWeekView = (
  displayedWeek,
  calendarDaysWithEvents,
  setWeekViewEventObject
) => {
  let blankArr = [];
  displayedWeek.forEach((date, index) => {
    if (calendarDaysWithEvents[format(date, "yyyy-MM-dd")]) {
      calendarDaysWithEvents[format(date, "yyyy-MM-dd")].events.forEach(
        (event) => {
          event.span = event.lessonLength / 5;
          let hours = getHours(new Date(event.dateTime));
          let minutes = getMinutes(new Date(event.dateTime));
          event.rowNumber = Math.round(hours * 12 + 3 + minutes / 5);
          event.rowLength = event.lessonLength / 5;
          event.index = index + 1;
          blankArr.push(event);
        }
      );
      calendarDaysWithEvents[format(date, "yyyy-MM-dd")].availabilities.forEach(
        (event) => {
          event.span = event.lessonLength / 5;
          let hours = getHours(new Date(event.dateTime));
          let minutes = getMinutes(new Date(event.dateTime));
          event.rowNumber = Math.round(hours * 12 + 3 + minutes / 5);
          event.rowLength = event.lessonLength / 5;
          event.index = index + 1;
          blankArr.push(event);
        }
      );
    }
  });
  setWeekViewEventObject([...blankArr]);
};

export const createEventObjectForDayView = (
  calendarDaysWithEvents,
  selectedDay,
  setInternalSelectedDay
) => {
  let correctDateFormat =
    typeof selectedDay.date === "string"
      ? convertDateStringIntoDateObject(selectedDay.date)
      : selectedDay.date;
  let blankArr = [];
  if (calendarDaysWithEvents[format(correctDateFormat, "yyyy-MM-dd")]) {
    calendarDaysWithEvents[
      format(correctDateFormat, "yyyy-MM-dd")
    ].events.forEach((event) => {
      event.span = event.lessonLength / 5;
      let hours = getHours(new Date(event.dateTime));
      let minutes = getMinutes(new Date(event.dateTime));
      event.rowNumber = Math.round(hours * 12 + 2 + minutes / 5);
      event.rowLength = event.lessonLength / 5;
      event.index = 0;
      blankArr.push(event);
    });
    calendarDaysWithEvents[
      format(correctDateFormat, "yyyy-MM-dd")
    ].availabilities.forEach((event) => {
      event.span = event.lessonLength / 5;
      let hours = getHours(new Date(event.dateTime));
      let minutes = getMinutes(new Date(event.dateTime));
      event.rowNumber = Math.round(hours * 12 + 2 + minutes / 5);
      event.rowLength = event.lessonLength / 5;
      event.index = 0;
      blankArr.push(event);
    });
  }
  setInternalSelectedDay({ ...selectedDay, events: blankArr });
};

export const selectFamilyForNewTeacherEvent = (fam, student, updateState) => {
  updateState((prev) => ({
    ...prev,
    familyAddress: fam.address,
    familyEmail: fam.primaryEmail,
    familyId: fam.familyId,
    familyName: fam.familyName,
    familyPhoneNumber: fam.primaryCellphone,
    name: `${student.studentName} ${fam.familyName}`,
    parentName: fam.primaryParentName,
    studentDOB: student.dob,
    studentId: student.studentId,
    studentName: student.studentName,
    waveCustomerId: fam.waveCustomerId,
  }));
};

export const convertSchoolAbbreviationToFullName = (abbreviation) => {
  switch (abbreviation.toUpperCase()) {
    case "MMA":
      return "Mountainside";
    case "LSMA":
      return "Lakeside";
    case "TMA":
      return "Treeside";
    case "MOR":
      return "Morningside";
    case "PSMA":
      return "Parkside";
    default:
      return abbreviation;
  }
};
